import {Button, Dialog, DialogFooter, Flag, Separator, TextInput} from 'platform/components';
import {Box, Hide, HStack, Stack, Text, VStack} from 'platform/foundation';

import {useCallback, useState} from 'react';

import {TestIdProps, suffixTestId} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '../../../../../types/api/api';
import {getPresetTags} from '../utils/getPresetTags';
import {BoundariesSection} from './BoundariesSection';
import {PropertiesSection} from './PropertiesSection';

const EMPTY_CONFIG: RelativeFilterConfigBody = {
  year: null,
  mileage: null,
  mileagePct: null,
  power: null,
  powerPct: null,
  price: null,
  pricePct: null,
  featureScore: null,
  trimLevel: null,
  transmission: null,
  fuel: null,
  body: null,
  drive: null,
  vatDeductible: null,
  sellerType: null,
  seats: null,
  radius: null,
};

interface PresetEditorDialogProps extends TestIdProps {
  isOpen: boolean;
  /** null means create mode */
  initialPreset: RelativeFilterResponseBody | null;
  onSave: (name: string, config: RelativeFilterConfigBody) => void;
  onDelete?: () => void;
  onClose: () => void;
}

export function PresetEditorDialog(props: PresetEditorDialogProps) {
  const isSystem = props.initialPreset?.system ?? false;
  const isReadOnly = isSystem;

  const [name, setName] = useState<string>(props.initialPreset?.name ?? '');
  const [config, setConfig] = useState<RelativeFilterConfigBody>(
    props.initialPreset?.config ?? EMPTY_CONFIG
  );

  const handleNameChange = useCallback((val: string | null) => {
    setName(val ?? '');
  }, []);

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0;

  const handleSave = useCallback(() => {
    if (!isNameValid) {
      return;
    }
    props.onSave(trimmedName, config);
  }, [trimmedName, isNameValid, config, props]);

  const summaryTags = getPresetTags(config);

  return (
    <Dialog
      title={i18n.t('entity.filterForm.savedFilters.dialog.title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      withAdditionalFooter
      scrollBehavior="inside"
      data-testid={suffixTestId('dialog', props)}
    >
      <VStack spacing={4}>
        <Stack direction={['column', 'row']} align="flex-start" justify="space-between" spacing={4}>
          <Box width={68} flexShrink={0}>
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.filterForm.savedFilters.dialog.presetName')}
              </Text>
              <TextInput
                value={name}
                onChange={handleNameChange}
                isDisabled={isSystem}
                isInvalid={!isNameValid}
                errorMessage={
                  isNameValid
                    ? undefined
                    : i18n.t('entity.filterForm.savedFilters.dialog.presetNameRequired')
                }
                data-testid={suffixTestId('nameInput', props)}
              />
            </VStack>
          </Box>
          {summaryTags.length > 0 ? (
            <HStack spacing={1} justify={[undefined, 'flex-end']} wrap>
              {summaryTags.map((label, index) => (
                <Flag
                  // We do not have unique tags, so we use the index as the key
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}-${label}`}
                  label={label}
                  size="small"
                  colorScheme="blue"
                  isSubtle
                />
              ))}
            </HStack>
          ) : (
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.filterForm.savedFilters.summary.noConstraints')}
            </Text>
          )}
        </Stack>

        <Separator spacing={0} />

        <Stack direction={['column', 'row']} spacing={[4, 0]}>
          <Box flexGrow={1}>
            <BoundariesSection
              value={config}
              onChange={(next) => setConfig((prev) => ({...prev, ...next}))}
              isReadOnly={isReadOnly}
            />
          </Box>
          <Separator orientation="vertical" spacing={4} />
          <Box flexGrow={1}>
            <PropertiesSection
              value={config}
              onChange={(next) => setConfig((prev) => ({...prev, ...next}))}
              isReadOnly={isReadOnly}
            />
          </Box>
        </Stack>
      </VStack>

      <DialogFooter data-testid={suffixTestId('footer', props)}>
        <HStack justify="space-between">
          <Hide when={isReadOnly || !props.onDelete}>
            <Button
              title={i18n.t('entity.filterForm.savedFilters.dialog.delete')}
              leftIcon="action/delete_outline"
              variant="dangerOutlined"
              onClick={props.onDelete}
              data-testid={suffixTestId('deleteButton', props)}
            />
          </Hide>
          <HStack spacing={2} justify="flex-end" grow={1}>
            {isReadOnly ? (
              <Button
                title={i18n.t('entity.filterForm.savedFilters.dialog.close')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('closeButton', props)}
              />
            ) : (
              <>
                <Button
                  title={i18n.t('entity.filterForm.savedFilters.dialog.cancel')}
                  variant="secondary"
                  onClick={props.onClose}
                  data-testid={suffixTestId('cancelButton', props)}
                />
                <Button
                  title={i18n.t('entity.filterForm.savedFilters.dialog.save')}
                  onClick={handleSave}
                  isDisabled={!isNameValid}
                  data-testid={suffixTestId('saveButton', props)}
                />
              </>
            )}
          </HStack>
        </HStack>
      </DialogFooter>
    </Dialog>
  );
}
